import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { sortBy } from "lodash";

export const TOPIC_COLORS = [
  { name: "blue", bg: "blue-6", fg: "grey-1" },
  { name: "green", bg: "green-7", fg: "grey-1" },
  { name: "orange", bg: "orange-7", fg: "grey-10" },
  { name: "purple", bg: "deep-purple-5", fg: "grey-1" },
  { name: "red", bg: "red-6", fg: "grey-1" },
  { name: "teal", bg: "teal-6", fg: "grey-1" },
  { name: "amber", bg: "amber-8", fg: "grey-10" },
  { name: "pink", bg: "pink-5", fg: "grey-1" },
  { name: "cyan", bg: "cyan-7", fg: "grey-10" },
  { name: "brown", bg: "brown-5", fg: "grey-1" },
];
deepFreeze(TOPIC_COLORS);

export const TOPIC_FIELDS = {
  title: "",
  saleID: null,
  color: "blue",
  archived: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(TOPIC_FIELDS);

export default class Topic extends DataStructure {
  constructor(projectID, id, data = {}) {
    data = sanitize(data, TOPIC_FIELDS);

    // `saleID` is the sale's letter, but topics saved by the first sale picker
    // hold the full state ID (projectID + letter).
    if (
      projectID &&
      data.saleID &&
      data.saleID.length > projectID.length &&
      data.saleID.startsWith(projectID)
    ) {
      data.saleID = data.saleID.slice(projectID.length);
    }

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.projectID = projectID || null;
    this.permissions = Object.freeze({
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  canBeEditedBy(user) {
    return user.isEmployee;
  }

  canBeDeletedBy(user) {
    return user.isEmployee && (user.id === this.data.createdBy || user.isAdmin);
  }

  // Database

  get path() {
    return Topic.getPath(this.projectID, this.id);
  }

  static getPath(projectID, id) {
    let path = `projects/${projectID}/topics`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(topic) {
    return topic.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Topic(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Formatting

  get colorDef() {
    return (
      TOPIC_COLORS.find((c) => c.name === this.data.color) || TOPIC_COLORS[0]
    );
  }

  // `saleID` is stored as the sale's letter; this is the full sale ID.
  get saleStateID() {
    return this.data.saleID ? this.projectID + this.data.saleID : null;
  }

  get messages() {
    return sortBy(
      Object.values(store.state.main.messages).filter(
        (m) => m.projectID === this.projectID && m.data.topicID === this.id
      ),
      "created"
    );
  }

  // Counting needs no sort (MessagePanel counts a whole thread's topics in one
  // pass instead of asking each topic).
  get messageCount() {
    return Object.values(store.state.main.messages).filter(
      (m) => m.projectID === this.projectID && m.data.topicID === this.id
    ).length;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  toString() {
    return this.data.title;
  }
}
